/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSession } from "../../context/AuthContext";
import CountCard from "./CountCard";
import api from "../../services/api";

const STATUSES = ["PENDING", "APPROVED", "REJECTED", "NOT_STARTED", "ASSIGNED", "RESOLVED", "SUPPORT_OPEN"];

export default function TaskList() {
  const navigate = useNavigate();
  const { data: session }: any = useSession();
  const userId = session?.user?.id;
  
  const [tasks, setTasks] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
 
  useEffect(() => {
    const fetchTasks = async () => {
      if (!userId) return;
      try {
        const res = await api.get(`/submissions/${userId}?t=${Date.now()}`);
        setTasks(res.data);
      } catch (err) {
        console.error("Failed to fetch tasks:", err);
      }
    };
    
    fetchTasks();
    const interval = setInterval(fetchTasks, 10000); // 10s sync
    return () => clearInterval(interval);
  }, [userId]);
 
  const filteredTasks = tasks.filter(task => {
    const matchesSearch = task.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
                         (task.module && task.module.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesType = !selectedType || task.type === selectedType;
    const matchesStatus = !selectedStatus || task.status === selectedStatus;
    return matchesSearch && matchesType && matchesStatus;
  });

  const stats = {
    pending: tasks.filter(t => t.status === "PENDING").length,
    completed: tasks.filter(t => t.status === "APPROVED" || t.status === "COMPLETED" || t.status === "RESOLVED").length,
    attention: tasks.filter(t => t.status === "REJECTED").length,
  };

  return (
    <div className="space-y-10 animate-in fade-in slide-in-from-bottom-4 duration-700">
      {/* Header & Stats */}
      <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center gap-6">
        <div className="whitespace-nowrap shrink-0">
          <nav className="flex flex-wrap items-center gap-2 mb-3">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 hover:text-primary transition-colors group"
            >
              <span className="material-symbols-outlined text-[14px] transition-transform group-hover:-translate-x-1">arrow_back</span>
              Back
            </button>
            <span className="text-slate-300 text-[10px]">/</span>
            <Link to="/dashboard" className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 hover:text-primary transition-colors">Academy</Link>
            <span className="text-slate-300 text-[10px]">/</span>
            <span className="text-[10px] font-black uppercase tracking-[0.2em] text-primary">Tasks</span>
          </nav>
          <h2 className="text-2xl font-black text-on-surface tracking-tighter">Your Tasks</h2>
          <p className="text-xs text-slate-500 font-medium mt-1">Keep track of the tasks you need to complete to grow your business.</p>
        </div>
        <div className="flex gap-3 w-full xl:w-auto overflow-x-auto p-6 -m-6 hide-scrollbar items-center">
          <CountCard 
            title="Under Review" 
            count={stats.pending} 
            label="Sent to Coach" 
            icon="update" 
            colorClass="text-blue-600"
          />
          <CountCard 
            title="Completed" 
            count={stats.completed} 
            label="Approved" 
            icon="verified" 
            colorClass="text-emerald-600"
          />
          <CountCard 
            title="Action Needed" 
            count={stats.attention} 
            label="Changes Needed" 
            icon="emergency_home" 
            colorClass="text-red-600"
          />
        </div>
      </div>
 
      {/* Filter Bar */}
      <div className="bg-surface-container-low p-4 rounded-2xl border border-outline-variant/10 shadow-sm space-y-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <span className="material-symbols-outlined absolute left-4 top-1/2 -translate-y-1/2 text-slate-400">inventory</span>
            <input 
               type="text" 
               placeholder="Search for tasks..."
               className="w-full pl-12 pr-4 py-3 bg-surface-container-lowest border border-outline-variant/10 rounded-xl focus:ring-2 focus:ring-primary/20 outline-none transition-all text-sm font-medium"
               value={searchQuery}
               onChange={(e) => setSearchQuery(e.target.value)}
             />
          </div>
          <div className="flex gap-2">
            <select 
              className="px-4 py-3 bg-surface-container-lowest text-on-surface border border-outline-variant/10 rounded-xl text-xs font-bold font-inter outline-none focus:ring-2 focus:ring-primary/20 transition-all"
              onChange={(e) => setSelectedType(e.target.value || null)}
            >
              <option value="">All Task Types</option>
              <option value="LINK">Submit a Link</option>
              <option value="IMAGE">Upload a Screenshot</option>
              <option value="TEXT">Write an Answer</option>
            </select>
            <select 
              className="px-4 py-3 bg-surface-container-lowest text-on-surface border border-outline-variant/10 rounded-xl text-xs font-bold font-inter outline-none focus:ring-2 focus:ring-primary/20 transition-all"
              onChange={(e) => setSelectedStatus(e.target.value || null)}
            >
              <option value="">All Statuses</option>
              {STATUSES.map(s => {
                let label = s.replace("_", " ");
                if (s === "PENDING") label = "Waiting for Review";
                if (s === "REJECTED") label = "Changes Needed";
                if (s === "SUPPORT_OPEN") label = "Coach Helping";
                if (s === "ASSIGNED") label = "Expert Assigned";
                if (s === "NOT_STARTED") label = "Not Started";
                if (s === "APPROVED" || s === "RESOLVED" || s === "COMPLETED") label = "Approved";
                return <option key={s} value={s}>{label}</option>;
              })}
            </select>
          </div>
        </div>
      </div>
 
      {/* Task List Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        {filteredTasks.length > 0 ? filteredTasks.map((task, idx) => (
          <div 
            key={task.id} 
            id={idx === 0 ? "tour-first-task" : undefined}
            data-task-id={task.id}
            onClick={() => navigate(`/tasks/${task.id}`)}
            className={`bg-surface-container-low border rounded-[2rem] p-7 transition-all hover:shadow-2xl hover:translate-y-[-6px] flex flex-col h-full group cursor-pointer border-t-8 ${
              task.status === "APPROVED" || task.status === "COMPLETED" || task.status === "RESOLVED" ? "border-emerald-500 bg-emerald-500/5 shadow-[0_20px_50px_rgba(16,185,129,0.05)]" :
              task.status === "REJECTED" ? "border-red-500 bg-red-500/5 shadow-[0_20px_50px_rgba(239,68,68,0.05)]" :
              task.hasEvidence ? "border-violet-600 bg-violet-600/5 shadow-[0_20px_50px_rgba(139,92,246,0.05)]" :
              task.status === "ASSIGNED" ? "border-blue-500 bg-blue-500/5 shadow-[0_20px_50px_rgba(59,130,246,0.05)]" :
              task.status === "PENDING" ? "border-amber-500 bg-amber-500/5 shadow-[0_20px_50px_rgba(245,158,11,0.05)]" : 
              "border-outline-variant/20"
            }`}
          >
            <div className="flex justify-between items-start mb-6">
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${
                  task.status === "APPROVED" || task.status === "COMPLETED" || task.status === "RESOLVED" ? "bg-emerald-500" :
                  task.status === "REJECTED" ? "bg-red-500" :
                  task.hasEvidence ? "bg-violet-600 shadow-[0_0_8px_rgba(139,92,246,0.5)]" :
                  task.status === "ASSIGNED" ? "bg-blue-500" :
                  task.status === "PENDING" ? "bg-amber-500" : "bg-slate-400"
                }`} />
                <span className={`text-[10px] font-black uppercase tracking-widest ${
                  task.status === "APPROVED" || task.status === "COMPLETED" || task.status === "RESOLVED" ? "text-emerald-500" :
                  task.status === "REJECTED" ? "text-red-500" :
                  task.hasEvidence ? "text-violet-600" :
                  task.status === "ASSIGNED" ? "text-blue-500" :
                  task.status === "PENDING" ? "text-amber-500" : "text-slate-400"
                }`}>
                  {task.status === "APPROVED" || task.status === "COMPLETED" || task.status === "RESOLVED" ? "APPROVED" :
                   task.status === "REJECTED" ? "CHANGES NEEDED" :
                   task.status === "PENDING" ? (task.hasEvidence ? "SENT FOR REVIEW" : "WAITING FOR YOU") :
                   task.status === "SUPPORT_OPEN" ? "COACH HELPING" :
                   task.status === "ASSIGNED" ? "EXPERT ASSIGNED" :
                   task.status.replace("_", " ")}
                </span>
              </div>
            </div>
 
            <div className="flex-1 space-y-1">
              <h4 className="text-xl font-bold text-on-surface tracking-tight group-hover:text-primary transition-colors">
                {task.title}
              </h4>
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-relaxed">
                {task.module}
              </p>
            </div>
 
            <div className="pt-6 border-t border-outline-variant/5 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="material-symbols-outlined text-sm text-on-surface-variant">
                  {task.type === "LINK" ? "link" : task.type === "IMAGE" ? "image" : "description"}
                </span>
                <span className="text-[10px] font-bold text-on-surface-variant uppercase tracking-tight">
                  {task.type === "LINK" ? "Submit Link" : task.type === "IMAGE" ? "Upload Image" : "Text Response"}
                </span>
              </div>
              <div className="text-[10px] font-bold text-on-surface-variant flex items-center gap-1.5">
                <span className="material-symbols-outlined text-sm">calendar_today</span>
                {task.dueDate}
              </div>
            </div>
          </div>
        )) : (
          <div className="col-span-full py-20 text-center space-y-4">
            <span className="material-symbols-outlined text-6xl text-slate-200">folder_open</span>
            <p className="text-slate-400 font-medium italic">No tasks found. Try changing your search or filters.</p>
          </div>
        )}
      </div>
    </div>
  );
}
